import * as React from 'react';
import axios from '../../../../utils/axios';
import { AvatarIniciais, Num } from '../../../../components/gps';
import { perfilLabel } from '../../../../utils/perfil';

/* ============================================================================
   Quem esta no sistema agora - SOMENTE MASTER.

   "Online" aqui significa uma coisa precisa: deu sinal de vida nos ultimos 150
   segundos. Cada aba aberta consulta `GET /geral/estado` a cada minuto (e a mesma
   chamada que detecta queda e versao nova), e o backend anota quem chamou.

   O QUE ISTO **NAO** E: nao e "tem sessao valida". O token vale horas; alguem que
   fechou o navegador continua com token bom e NAO aparece aqui, que e o
   comportamento certo para a pergunta "quem esta trabalhando agora".

   TRES LIMITES, ditos na propria tela para ninguem tirar conclusao errada:
     - quem esta com a aba em SEGUNDO PLANO nao pinga (o laco para em
       `document.hidden`), entao pode nao aparecer;
     - o reinicio da API zera a lista - ela se reconstroi em ate um minuto;
     - a contagem e por USUARIO, nao por aba: a mesma conta em dois computadores
       aparece uma vez (o IP mostrado e o do ultimo sinal).

   A restricao de perfil vale no SERVIDOR (o endpoint recusa quem nao e Master);
   esconder o bloco aqui e so para nao mostrar um erro a quem nao deveria nem ver
   a existencia disto.
   ============================================================================ */

type Online = {
  id: number;
  nome: string | null;
  email: string;
  perfil: number;
  desde: string;
  visto: string;
  ip: string | null;
};

const INTERVALO_MS = 45_000;

function haQuantoTempo(iso: string) {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const seg = Math.round(ms / 1000);
  if (seg < 60) return 'agora';
  const min = Math.round(seg / 60);
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  return `há ${h}h ${min % 60}min`;
}

export default function UsuariosOnline() {
  const [lista, setLista] = React.useState<Online[]>([]);
  const [erro, setErro] = React.useState('');
  const [carregando, setCarregando] = React.useState(true);

  React.useEffect(() => {
    let vivo = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const buscar = async () => {
      try {
        const { data } = await axios.get<{ usuarios: Online[] }>('/geral/online');
        if (!vivo) return;
        setLista(Array.isArray(data?.usuarios) ? data.usuarios : []);
        setErro('');
      } catch (e: any) {
        if (!vivo) return;
        setErro(e?.response?.data?.message || 'Não foi possível ler quem está online.');
      } finally {
        if (vivo) {
          setCarregando(false);
          // 45s e menor que a janela de 150s do servidor: a lista sempre reflete
          // pelo menos o penultimo ciclo, e ninguem "pisca" por sincronia ruim.
          timer = setTimeout(buscar, INTERVALO_MS);
        }
      }
    };

    buscar();
    return () => {
      vivo = false;
      clearTimeout(timer);
    };
  }, []);

  return (
    <section className="overflow-hidden rounded-lg border border-primary/25 bg-surface shadow-card">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-primary/20 bg-primary/10 px-4 py-2">
        <h2 className="text-[12px] font-bold uppercase tracking-[0.06em] text-primary">No sistema agora</h2>
        <span className="inline-flex items-center gap-1.5 text-[11px] text-primary">
          <span className="h-1.5 w-1.5 rounded-full bg-ok" aria-hidden />
          <Num className="font-bold">{lista.length}</Num>
        </span>
      </header>

      <div className="p-4">
        {carregando && <p className="text-[12px] text-muted-foreground">Carregando...</p>}

        {!carregando && erro && <p className="text-[12px] text-danger">{erro}</p>}

        {!carregando && !erro && lista.length === 0 && (
          <p className="text-[12px] text-muted-foreground">
            Ninguém com a tela aberta neste minuto — só você.
          </p>
        )}

        {!carregando && !erro && lista.length > 0 && (
          /* ALTURA LIMITADA (29/09/2026, relatado: "aqui esta quebrado").

             A lista nao tinha teto. Com 31 pessoas online ela descia ate quase
             mil pixels, enquanto o painel ao lado terminava na metade - a Home
             ficava com uma coluna gigante e um vazio do lado direito.

             O teto e o mesmo do bloco "Logins de hoje", que ja rolava dentro de
             260px. Duas listas irmas com comportamentos diferentes era parte do
             que fazia a tela parecer quebrada. */
          <ul className="flex max-h-[420px] flex-col divide-y divide-line overflow-y-auto">
            {lista.map((u) => (
              <li key={u.id} className="flex items-center gap-2.5 py-2 first:pt-0 last:pb-0">
                <AvatarIniciais nome={u.nome || u.email || '?'} />

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[12px] font-semibold">{u.nome || u.email}</span>
                  <span className="block truncate text-[11px] text-muted-foreground">
                    {u.nome ? u.email : perfilLabel(u.perfil)}
                    {u.ip ? ' · ' : ''}
                    {u.ip && <Num className="text-faint">{u.ip}</Num>}
                  </span>
                </span>

                <span className="flex-none text-right">
                  <span className="block text-[11px] font-semibold text-ok">{haQuantoTempo(u.visto)}</span>
                  <span className="block text-[10px] text-faint">desde {haQuantoTempo(u.desde)}</span>
                </span>
              </li>
            ))}
          </ul>
        )}

        {/* A NOTA DE RODAPE SAIU (10/09/2026, a pedido).

            Ela explicava as tres condicoes da contagem - janela de 150s, aba em
            segundo plano que para de avisar, e a lista que zera quando o
            servidor reinicia. E verdade e continua valendo, mas ocupava quatro
            linhas embaixo de um cartao que tem tres nomes: mais texto que dado.

            A ressalva nao se perdeu, so mudou de lugar: o cabecalho deste
            arquivo a mantem por escrito, e e la que alguem vai olhar ao
            perguntar "por que fulano nao aparece?". */}
      </div>
    </section>
  );
}
